/* eslint-disable complexity */
/* eslint-disable new-cap */
import { Blueprint, ComponentControllerBase, ControllerRootOptions, IControllerBlueprint, Kitchen, KitchenData, Publish } from '@rta/rta-blueprint-library';

import { ControllerDataIn } from './types/controller-data.type';
import { DesignerOptions } from './designer-options.class';
import { getOverheadViewRotation } from '../helpers/get-overhead-view-rotation';
import { Navigation } from './navigation.class';
import { RenderLayerManagerClass } from '@r-services/render-layers';
import { ViewportServiceClass } from '@r-services/viewport';

export class ComponentController extends ComponentControllerBase<ControllerDataIn> implements IControllerBlueprint {
	#blueprint: Blueprint | null;
	#forceRenderCount: number;
	// 	#isZooming: boolean;
	#kitchen: Kitchen | null;
	// #kitchenVersion: number | null;
	#rebuildKitchen: (args) => void;
	#rebuildKitchenArgs: unknown[];
	#kitchenEventSubIds: Array<string> = [];
	#navigationSubIds: Array<string> = [];
	#selfSubIds: Array<string> = [];
	#destroyed = false;
	#autoZoomTimer: ReturnType<typeof setTimeout> | null = null;

	readonly options: DesignerOptions;
	readonly navigation: Navigation;
	readonly viewport: ViewportServiceClass;
	readonly renderLayer: RenderLayerManagerClass;

	// Viewport state accessor
	get isZooming (): boolean {
		return this.viewport.isZooming;
	}

	constructor (blueprint?: Blueprint, options: ControllerRootOptions<ControllerDataIn> = {}) {
		super('bp-controller', blueprint || null, { eventGroup: 'bp-component', ...options });

		this.#blueprint = blueprint || null;
		this.#forceRenderCount = 0;
		this.#kitchen = blueprint?.kitchens.getActiveVersion() || null;
		// this.#kitchenVersion = this.#kitchen?.version ?? null;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		this.#rebuildKitchen = (...args) => { this.#forceRenderCount++; };
		this.#rebuildKitchenArgs = [];

		this.options = new DesignerOptions({ dataIn: options.dataIn?.options ?? {}, parent: this }, this);
		this.navigation = new Navigation({
			dataIn: {
				islandId: this.#kitchen ? this.#kitchen?.design.islands.getDefaultIsland()?.id ?? null : null,
				panelId: this.#kitchen ? this.#kitchen?.design.islands.getDefaultIsland()?.frontPanel.id ?? null : null,
				...(options.dataIn?.navigation ?? {})
			},
			parent: this
		}, this);

		// Initialize viewport service with designer options configuration
		const viewportConfig = options.dataIn?.viewport ?? {};
		this.viewport = new ViewportServiceClass(
			{
				...viewportConfig
			},
			() => this.render() // Pass render callback
		);
		this.renderLayer = new RenderLayerManagerClass(options.dataIn?.renderLayer ?? {});

		// Set up event bridges for services to publish changes
		this._setupServiceEventBridges();

		// Set up event listeners to trigger autozoom on content changes
		this._setupContentChangeEventListeners();

		// Blueprint View and the capture renderer build controllers with the blueprint passed here
		// rather than through the setter, so the view angle has to be settled in both places.
		this._syncOverheadViewRotation(false);

		this.setInitialized();
	}

	get blueprint (): Blueprint | null { return this.#blueprint; }

	/**
	 * Set the blueprint for this controller
	 * @param blueprint - The blueprint to set, or null to clear
	 */
	@Publish('blueprint', 'updated')
	set blueprint (blueprint: Blueprint | null) {
		this.#blueprint = blueprint;
		this.#kitchen = blueprint?.kitchens.getActiveVersion() || null;
		// this.#kitchenVersion = this.#kitchen?.version ?? null;

		// Update navigation with new kitchen data if available
		if (this.#kitchen) {
			this.navigation.islandId = this.#kitchen.design.islands.getDefaultIsland()?.id ?? null;
			this.navigation.panelId = this.#kitchen.design.islands.getDefaultIsland()?.frontPanel.id ?? null;
			
			// Set up kitchen event listeners for autozoom
			this._setupKitchenEventListeners();
			this._syncOverheadViewRotation(false);
		} else {
			this.navigation.islandId = null;
			this.navigation.panelId = null;
		}
	}

	get forceRenderCount (): number { return this.#forceRenderCount; }
	// get isZooming (): boolean { return this.#isZooming; }
	get kitchen (): Kitchen {
		return this.#kitchen;
	}

	// set readonly (value: boolean) { this.options.readonly = value; }
	get readonly (): boolean {
		// console.log('kv:controller.readonly', this.kitchen?.version, this.kitchen?.baseVersion, this.options.readonly);
		// force readonly if kitchen version !== null (meaning not a draft)
		return (this.kitchen?.version ?? null) === null ? this.options.readonly : true;
		// return this.options.readonly;
	}

	@Publish('kitchen_version', 'updated')
	set kitchenVersion (version: number | null) {
		let kv = this.#blueprint.kitchens.findVersion(version);
		if (!kv) {
			if (version === null) {
				kv = this.#blueprint.kitchens.newVersionFromLatest();
				kv.isTemp = true;
			} else {
				kv = this.#blueprint.kitchens.getActiveVersion();
			}
		}
		this.#kitchen = kv;
		// this.#kitchenVersion = version;

		// re-bind kitchen listeners so autozoom/content events track the swapped instance
		this._setupKitchenEventListeners();
		this._syncOverheadViewRotation(false);
	}
	get kitchenVersion (): number | null { return this.#kitchen.version; }
	// get kitchenVersion (): number | null { return this.#kitchenVersion; }

	// required for iu
	// @Publish('zoom', 'updated')
	// set zoom (zoom: number) {
	// 	const targetZoomFactor = zoom / 10;
	// 	if (this.options.viewport.zoomFactor !== targetZoomFactor) {
	// 		this.options.viewport.zoomFactor = targetZoomFactor;
	// 	}
	// }
	// get zoom (): number { return this.options.viewport.zoomFactor * 10; }

	// set rotation (rotation: number) { this.options.viewport.rotation = rotation; }
	// get rotation (): number { return this.options.viewport.rotation }

	// @Publish('image', 'snapshot')
	// eslint-disable-next-line class-methods-use-this
	public getImage = (): string | null => { return null; }

	public rebuildKitchen (): void {
		this.#rebuildKitchen(this.#rebuildKitchenArgs);
	}

	public setRebuild (fn: (args) => void, ...args: unknown[]): void {
		this.#rebuildKitchen = fn;
		this.#rebuildKitchenArgs = args;
	}

	public render (): void {
		this.#forceRenderCount++;
	}

	/**
	 * Create a new blueprint with a default kitchen
	 * @param kitchenData - Optional kitchen data to initialize with
	 * @returns The newly created blueprint
	 */
	public createBlueprint (kitchenData?: Partial<KitchenData>): Blueprint {
		const blueprint = new Blueprint();

		const kitchen = new Kitchen();
		blueprint.kitchens.addKitchen(kitchen);

		// Set this blueprint as the controller's blueprint
		this.blueprint = blueprint;

		return blueprint;
	}

	/**
	 * Releases every subscription and timer this controller holds. Required for the short-lived
	 * controllers the Blueprint View builds per page/card: without it each one keeps a permanent
	 * ('*','*') subscriber on the SHARED kitchen, so they accumulate on every export and only a
	 * page reload clears them.
	 */
	public destroy (): void {
		if (this.#destroyed) return;
		this.#destroyed = true;

		if (this.#autoZoomTimer !== null) {
			clearTimeout(this.#autoZoomTimer);
			this.#autoZoomTimer = null;
		}

		this.#kitchenEventSubIds.forEach((sId) => this.#kitchen?.unsubscribe(sId));
		this.#kitchenEventSubIds = [];

		this.#navigationSubIds.forEach((sId) => this.navigation.unsubscribe(sId));
		this.#navigationSubIds = [];

		this.#selfSubIds.forEach((sId) => this.unsubscribe(sId));
		this.#selfSubIds = [];
	}

	get destroyed (): boolean { return this.#destroyed }

	/**
	 * Set up event bridges for services to publish changes to the controller
	 * This allows services to publish events without tight coupling to @Publish decorator
	 */
	private _setupServiceEventBridges (): void {
		// Create event bridge that forwards service events to controller's publish method
		const eventBridge = {
			publish: (noun: string, action: string, payload?: any) => {
				// Convert service payload to EventPayloadAction format
				const eventPayload = {
					ref: null,
					data: payload ? { serviceData: payload } : null
				};
				this.publish(noun, action, eventPayload);
			}
		};

		// Set up event bridge for viewport service
		this.viewport.setEventBridge(eventBridge);

		// Set up event bridge for render layer manager
		this.renderLayer.setEventBridge(eventBridge);
	}

	/**
	 * Set up event listeners to trigger autozoom on content changes
	 * This ensures autozoom fires whenever kitchen design content changes (add, update, delete)
	 * including appliances, islands, panels, breakpoints, cutouts, etc.
	 * Also includes navigation changes (displayMode, islandId, panelId)
	 */
	private _setupContentChangeEventListeners (): void {
		// Set up navigation event listeners (these are always needed)
		this._setupNavigationEventListeners();

		// Set up kitchen event listeners if kitchen is available
		this._setupKitchenEventListeners();

		// Listen for blueprint changes (new kitchen versions, etc.)
		const sId = this.subscribe('blueprint', 'updated', () => {
			this._onContentChange('blueprint');
		});
		if (sId) this.#selfSubIds.push(sId);
	}

	/**
	 * Set up navigation event listeners to trigger autozoom when navigation properties change
	 * This ensures autozoom fires whenever displayMode, islandId, or panelId changes
	 */
	private _setupNavigationEventListeners (): void {
		const subIds = [
			// Listen for navigation display mode changes
			this.navigation.subscribe('navigation-display-mode', 'updated', () => {
				this._onContentChange('navigation-display-mode');
			}),

			// Listen for navigation island changes
			this.navigation.subscribe('navigation-island', 'updated', () => {
				this._onContentChange('navigation-island');
			}),

			// Listen for navigation panel changes
			this.navigation.subscribe('navigation-panel', 'updated', () => {
				this._onContentChange('navigation-panel');
			})
		];

		subIds.forEach((sId) => { if (sId) this.#navigationSubIds.push(sId); });
	}

	/**
	 * Set up kitchen event listeners for autozoom
	 * This is called when a kitchen is set or updated
	 */
	private _setupKitchenEventListeners (): void {
		// Unsubscribe old listeners before re-subscribing (prevents duplicates on setBlueprint)
		if (this.#kitchenEventSubIds.length > 0) {
			this.#kitchenEventSubIds.forEach((sId) => this.#kitchen?.unsubscribe(sId));
			this.#kitchenEventSubIds = [];
		}

		if (this.#kitchen) {
			const sId = this.#kitchen.subscribe('*', '*', (event) => {
				// Only trigger autozoom for content-affecting events
				if (this._shouldTriggerAutoZoomForEvent(event)) {
					this._onContentChange(event.noun);
				}
			});
			if (sId) this.#kitchenEventSubIds.push(sId);
		}
	}

	/**
	 * Determine if an event should trigger autozoom
	 * This filters out events that don't affect the visual content
	 */
	private _shouldTriggerAutoZoomForEvent (event: any): boolean {
		// Skip events that don't affect visual content
		const skipEvents = [
			'viewport-state',
			'viewport-config', 
			'viewport-container-size',
			'viewport-viewer-size',
			'render-layer',
			'render-layer-config'
		];

		// Skip if it's a viewport or render layer event
		if (skipEvents.includes(event.noun)) {
			return false;
		}

		// Skip if it's a read-only operation
		if (event.action === 'read' || event.action === 'get') {
			return false;
		}

		// Trigger for all other events (add, update, delete, create, remove, etc.)
		return true;
	}

	/**
	 * Keep the overhead viewport square-on with whatever the kitchen now holds.
	 * Snaps on load -- there is no previous view to ease away from, and a backgrounded tab
	 * has no animation frames to run the ease with. Eases when the design changes under the user.
	 * @param smooth animate the turn rather than jumping to it
	 */
	private _syncOverheadViewRotation (smooth: boolean): void {
		const target = getOverheadViewRotation(this.#kitchen);
		if (this.viewport.config.rotation === target) return;

		if (smooth) this.viewport.actions.smoothRotation(target);
		else this.viewport.actions.setRotation(target);
	}

	/**
	 * React to content changes: square the overhead view, then trigger autozoom if it's enabled
	 */
	private _onContentChange (eventType: string): void {
		if (this.#destroyed) return;

		this._syncOverheadViewRotation(true);

		// Only trigger autozoom if it's enabled in the viewport config
		if (this.viewport.config.autoZoom || this.viewport.config.autoZoomOverhead) {
			// Debounce: coalesce rapid-fire events into a single autozoom
			if (this.#autoZoomTimer !== null) {
				clearTimeout(this.#autoZoomTimer);
			}
			this.#autoZoomTimer = setTimeout(() => {
				this.#autoZoomTimer = null;
				this.viewport.actions.triggerAutoZoom();
			}, 0);
		}
	}
}
